import { useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineMobileAndroidIcon from 'LuxKit/Icons/line/LineMobileAndroidIcon'
import LineChatIcon from 'LuxKit/Icons/line/LineChatIcon'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import { formatExperienceVoucherTypes } from 'lib/experiences/formatExperienceVoucherTypes'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceInstantConfirmationText from 'components/Experiences/ExperienceTiles/ExperienceInstantConfirmationText'
import GrowthInclusionList from './GrowthInclusionList'

interface Props {
  experience: App.ExperienceOffer
}

/**
 * ExperienceOfferCard's control layout uses descriptive copy. The Growth examples
 * replace that copy with voucher, language, duration and cancellation facts.
 * Reuse formatters from components/Experiences/ExperienceOfferPage/ExperienceOfferPageOverview.tsx
 * and the existing cancellation/confirmation components rather than recreating their rules.
 * The current example order is capped by GrowthInclusionList; priority when facts
 * overflow (especially cancellation) is still awaiting product confirmation.
 */
function GrowthExperienceInclusions({ experience }: Props) {
  const intl = useIntl()
  return <GrowthInclusionList>
    {experience.voucher.typesAccepted.length > 0 && <BodyTextBlock key="voucher" variant="small" lineClamp={1} startIcon={<LineMobileAndroidIcon size="XS" />}>
      {intl.formatMessage({
        id: 'oAh5GQJx50SrgeGLR2e4',
        defaultMessage: '{voucherTypes} {count, plural, one {voucher} other {vouchers}} accepted',
        description: 'Voucher types accepted for experience on PDP sidebar',
      }, { voucherTypes: formatExperienceVoucherTypes(experience.voucher.typesAccepted, intl), count: experience.voucher.typesAccepted.length })}
    </BodyTextBlock>}
    {experience.languages.length > 0 && <BodyTextBlock key="language" variant="small" lineClamp={1} startIcon={<LineChatIcon size="XS" />}>
      {intl.formatMessage({
        id: '7E2kR-S-Z8EIPxfr_Ton',
        defaultMessage: 'Language: {languages}',
        description: 'Languages supported by the experience shown on the homepage offer tile',
      }, { languages: intl.formatList(experience.languages.map(language => language.name)) })}
    </BodyTextBlock>}
    <BodyTextBlock key="duration" variant="small" lineClamp={1} startIcon={<LineClockFiveIcon size="XS" />}>
      {intl.formatMessage({
        id: 'rYUGzXiob1cfdPXLuKdR',
        defaultMessage: 'Duration: {duration}',
        description: 'Experience duration shown in the highlights on the homepage offer tile',
      }, { duration: getExperienceDurationString(experience, intl) })}
    </BodyTextBlock>
    {!!experience.copy.cancellationInfo?.refundPolicies?.length && <ExperienceCancellationText key="cancellation" cancellationInfo={experience.copy.cancellationInfo} textSize="small" lineClamp={1} />}
    {experience.hasInstantConfirmation && <ExperienceInstantConfirmationText key="confirmation" textSize="small" lineClamp={1} />}
  </GrowthInclusionList>
}

export default GrowthExperienceInclusions
